/**
 * AutoReveal Modal System
 * Handles automatic popup display with cookie-based tracking
 */

class AutoReveal {
  constructor(element) {
    this.element = jQuery(element);
    if (!this.element.length) return null;

    // DOM elements
    this.closeEl = this.element.find('button.close-button');

    // Configuration from data attributes
    this.cookieId = this.element.attr('data-cookie-id');
    this.latency = parseInt(this.element.attr('data-cookie-latency')) || 0;
    this.isTracked = this.element.attr('data-trace') === 'tracked';

    // Cookie settings
    this.cookieName = 'wav_modals_seen';
    this.maxAge = 28 * 24 * 60 * 60; // 28 days in seconds
  }

  /**
   * Get array of already seen modal IDs from cookie
   * @returns {Array<string>}
   */
  getSeenIds() {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(c => c.startsWith(this.cookieName + '='));

    if (!cookie) return [];

    try {
      const value = decodeURIComponent(cookie.split('=')[1]);
      return JSON.parse(value);
    } catch(e) {
      console.warn('Failed to parse modal cookie:', e);
      return [];
    }
  }

  /**
   * Check if this modal has already been seen
   * @returns {boolean}
   */
  hasBeenSeen() {
    // Untracked modals are always shown
    if (!this.isTracked || !this.cookieId) {
      return false;
    }

    return this.getSeenIds().includes(this.cookieId);
  }

  /**
   * Mark this modal as seen by saving to cookie
   */
  markAsSeen() {
    // Don't track if not configured for tracking
    if (!this.isTracked || !this.cookieId) {
      return;
    }

    const seenIds = this.getSeenIds();

    // Already marked as seen
    if (seenIds.includes(this.cookieId)) {
      return;
    }

    // Add current ID to array
    seenIds.push(this.cookieId);

    // Save to cookie
    const cookieValue = encodeURIComponent(JSON.stringify(seenIds));
    const cookieString = `${this.cookieName}=${cookieValue}; max-age=${this.maxAge}; path=/; SameSite=Lax`;

    document.cookie = cookieString;
  }

  /**
   * Show the modal after configured latency
   */
  show() {
    setTimeout(() => {
      this.element.fadeIn(300);
    }, this.latency);
  }

  /**
   * Close and remove the modal
   */
  close() {
    this.element.fadeOut(300, () => {
      this.element.remove();
    });

    // Mark as seen when user closes it
    this.markAsSeen();
  }

  /**
   * Initialize the modal behavior
   */
  init() {
    // Don't show if already seen
    if (this.hasBeenSeen()) {
      this.element.remove();
      return;
    }

    // Show modal
    this.show();

    // Bind close button
    this.closeEl.on('click', (e) => {
      e.preventDefault();
      this.close();
    });

    // Optional: Close on overlay click
    this.element.on('click', (e) => {
      if (e.target === this.element[0]) {
        this.close();
      }
    });

    // Optional: Close on ESC key
    jQuery(document).on('keydown.autoreveal', (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.close();
        jQuery(document).off('keydown.autoreveal');
      }
    });
  }
}

/**
 * Initialize all auto-reveal modals on the page
 */
function initAutoReveal() {
  const modals = jQuery('.reveal-auto');

  if (!modals.length) return;

  modals.each(function() {
    const reveal = new AutoReveal(this);
    if (reveal && reveal.element) {
      reveal.init();
    }
  });
}
